import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, BACKEND_URL } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  CreditCard, CheckCircle, Clock, AlertTriangle, XCircle,
  Calendar, Shield, Zap, ArrowRight, Lock, Eye, EyeOff
} from "lucide-react";

const MiSuscripcion = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [pwForm, setPwForm] = useState({ password_actual: "", password_nueva: "", confirmar: "" });
  const [pwSubmitting, setPwSubmitting] = useState(false);

  const fetchMiSuscripcion = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mi-suscripcion`);
      setData(res.data);
      setLoading(false);
    } catch { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchMiSuscripcion();
    const interval = setInterval(fetchMiSuscripcion, 15000);
    return () => clearInterval(interval);
  }, [fetchMiSuscripcion]);

  const handleChangePassword = async () => {
    if (pwForm.password_nueva !== pwForm.confirmar) {
      toast.error("Las contrasenas no coinciden");
      return;
    }
    if (pwForm.password_nueva.length < 6) {
      toast.error("Minimo 6 caracteres");
      return;
    }
    setPwSubmitting(true);
    try {
      await axios.put(`${API}/auth/cambiar-password`, {
        password_actual: pwForm.password_actual,
        password_nueva: pwForm.password_nueva,
      });
      toast.success("Contrasena actualizada correctamente");
      setChangingPw(false);
      setPwForm({ password_actual: "", password_nueva: "", confirmar: "" });
      // Refresh data to clear must_change_password
      const res = await axios.get(`${API}/mi-suscripcion`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cambiar contrasena");
    } finally {
      setPwSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-emerald-800 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-16">
        <AlertTriangle className="w-12 h-12 mx-auto text-stone-300 mb-4" />
        <p className="text-stone-500">No se pudo cargar la informacion</p>
      </div>
    );
  }

  const statusConfig = {
    active: { label: "Activo", color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle, desc: "Tu cuenta esta activa y con acceso completo a tu plan." },
    trial: { label: "Periodo de prueba", color: "bg-blue-100 text-blue-800 border-blue-200", icon: Clock, desc: "Estas en periodo de prueba. Aprovecha para conocer todas las funciones." },
    pending: { label: "Pendiente de aprobacion", color: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock, desc: "Tu solicitud esta siendo revisada. Te notificaremos cuando tu plan este activo." },
    under_review: { label: "En revision", color: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock, desc: "Estamos revisando tu comprobante de pago. Pronto activaremos tu plan." },
    expired: { label: "Plan expirado", color: "bg-red-100 text-red-800 border-red-200", icon: AlertTriangle, desc: "Tu suscripcion ha expirado. Renueva para seguir usando todas las funciones." },
    suspended: { label: "Suspendido", color: "bg-stone-200 text-stone-600 border-stone-300", icon: XCircle, desc: "Tu cuenta ha sido suspendida. Contacta al administrador." },
    rejected: { label: "Rechazado", color: "bg-red-100 text-red-800 border-red-200", icon: XCircle, desc: "Tu solicitud fue rechazada. Contacta al administrador para mas informacion." },
  };

  const status = data.suscripcion_status || "active";
  const cfg = statusConfig[status] || statusConfig.active;
  const StatusIcon = cfg.icon;
  const isSuperadmin = data.role === "superadmin";

  const diasRestantes = data.dias_restantes;
  const progressPct = diasRestantes !== null && diasRestantes !== undefined
    ? Math.max(0, Math.min(100, (diasRestantes / 15) * 100))
    : 100;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl text-stone-900" data-testid="mi-suscripcion-title">Mi cuenta</h1>
        <p className="text-sm text-stone-500 mt-1">Informacion de tu suscripcion y plan</p>
      </div>

      {/* Account not activated alert */}
      {data.cuenta_activada === false && !isSuperadmin && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-sm p-4 flex items-start gap-3" data-testid="alert-activar-cuenta">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-amber-900">Cuenta pendiente de activacion</p>
            <p className="text-sm text-amber-700 mt-0.5">Revisa tu correo o solicita el enlace de activacion al administrador.</p>
          </div>
        </div>
      )}

      {/* Status Card */}
      <Card className={`border-2 ${cfg.color.split(" ").find(c => c.startsWith("border-")) || "border-stone-200"}`}>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${cfg.color.replace("border-", "bg-").split(" ").slice(0, 1).join(" ")}`}>
                <StatusIcon className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${cfg.color}`} data-testid="status-badge">
                    {cfg.label}
                  </span>
                  {isSuperadmin && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      Super Admin
                    </span>
                  )}
                </div>
                <p className="text-sm text-stone-600 mt-2">{cfg.desc}</p>
              </div>
            </div>
          </div>

          {/* Progress bar for trial/active */}
          {diasRestantes !== null && diasRestantes !== undefined && !isSuperadmin && (
            <div className="mt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-stone-500">Tiempo restante</span>
                <span className={`font-medium ${diasRestantes <= 5 ? "text-red-600" : "text-stone-900"}`} data-testid="dias-restantes">
                  {diasRestantes} día{diasRestantes !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="w-full bg-stone-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${diasRestantes <= 5 ? "bg-red-500" : diasRestantes <= 10 ? "bg-amber-500" : "bg-emerald-500"}`}
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}

          {status === "expired" && (
            <div className="mt-4">
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="btn-renovar">
                <CreditCard className="w-4 h-4 mr-2" /> Renovar suscripcion
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plan Details */}
      <Card className="border-stone-200">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg text-stone-900 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-700" />
              {isSuperadmin ? "Acceso completo" : `Plan ${data.plan_nombre || "Sin plan"}`}
            </h2>
            {data.plan_precio && !isSuperadmin && (
              <span className="text-lg font-display text-emerald-800">${data.plan_precio}/{data.plan_periodo}</span>
            )}
          </div>

          {/* Dates */}
          {!isSuperadmin && (
            <div className="grid grid-cols-2 gap-4 py-3 border-y border-stone-100">
              <div>
                <p className="text-xs text-stone-400">Fecha de inicio</p>
                <p className="text-sm font-medium text-stone-700 mt-0.5">
                  {data.subscription_start || data.trial_start
                    ? new Date(data.subscription_start || data.trial_start).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })
                    : "—"
                  }
                </p>
              </div>
              <div>
                <p className="text-xs text-stone-400">{status === "trial" ? "Termina prueba" : "Proxima renovacion"}</p>
                <p className="text-sm font-medium text-stone-700 mt-0.5">
                  {data.subscription_end || data.trial_end
                    ? new Date(data.subscription_end || data.trial_end).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })
                    : "—"
                  }
                </p>
              </div>
            </div>
          )}

          {/* Features */}
          <div>
            <p className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-3">
              {isSuperadmin ? "Acceso a todo el sistema" : "Incluido en tu plan"}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(data.plan_features || []).map((f) => (
                <div key={f} className="flex items-center gap-2 text-sm text-stone-700">
                  <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                  {f}
                </div>
              ))}
              {isSuperadmin && (
                <div className="flex items-center gap-2 text-sm text-stone-700">
                  <Shield className="w-4 h-4 text-purple-600 shrink-0" />
                  Todos los modulos y funciones
                </div>
              )}
            </div>
          </div>

          {/* Modules access */}
          {!isSuperadmin && data.plan_modulos && (
            <div>
              <p className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-3">Modulos habilitados</p>
              <div className="flex flex-wrap gap-1.5">
                {data.plan_modulos.map((m) => (
                  <span key={m} className="px-2 py-1 bg-emerald-50 text-emerald-800 rounded text-xs font-medium">
                    {m}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Limits */}
          {!isSuperadmin && data.plan_limites && Object.keys(data.plan_limites).length > 0 && (
            <div>
              <p className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-3">Limites</p>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(data.plan_limites).map(([key, val]) => (
                  <div key={key} className="bg-stone-50 rounded-sm p-3 text-center">
                    <p className="text-lg font-display text-stone-900">{val === -1 ? "Sin limite" : val}</p>
                    <p className="text-xs text-stone-500 capitalize">{key}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card className="border-stone-200">
        <CardContent className="p-6 space-y-4">
          <h2 className="font-display text-lg text-stone-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-stone-600" />
            Datos de la cuenta
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-stone-400">Nombre</p>
              <p className="text-sm font-medium text-stone-700">{data.nombre}</p>
            </div>
            <div>
              <p className="text-xs text-stone-400">Email</p>
              <p className="text-sm font-medium text-stone-700">{data.email}</p>
            </div>
          </div>

          {/* Change password */}
          {!changingPw ? (
            <Button variant="outline" size="sm" className="border-stone-300" onClick={() => setChangingPw(true)} data-testid="btn-cambiar-password">
              <Lock className="w-3.5 h-3.5 mr-1.5" /> Cambiar contrasena
            </Button>
          ) : (
            <div className="space-y-3 bg-stone-50 rounded-sm p-4" data-testid="form-cambiar-password">
              <h3 className="text-sm font-medium text-stone-900">Cambiar contrasena</h3>
              {!data.must_change_password && (
                <div className="space-y-1">
                  <Label className="text-xs">Contrasena actual</Label>
                  <div className="relative">
                    <Input type={showPassword ? "text" : "password"} value={pwForm.password_actual}
                      onChange={(e) => setPwForm(p => ({ ...p, password_actual: e.target.value }))}
                      className="border-stone-300 pr-10" data-testid="input-pw-actual" />
                    <button onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-xs">Nueva contrasena</Label>
                <Input type="password" value={pwForm.password_nueva}
                  onChange={(e) => setPwForm(p => ({ ...p, password_nueva: e.target.value }))}
                  className="border-stone-300" data-testid="input-pw-nueva" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Confirmar contrasena</Label>
                <Input type="password" value={pwForm.confirmar}
                  onChange={(e) => setPwForm(p => ({ ...p, confirmar: e.target.value }))}
                  className="border-stone-300" data-testid="input-pw-confirmar" />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleChangePassword} disabled={pwSubmitting} className="btn-primary" data-testid="btn-guardar-password">
                  {pwSubmitting ? "Guardando..." : "Guardar"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setChangingPw(false); setPwForm({ password_actual: "", password_nueva: "", confirmar: "" }); }} className="border-stone-300">
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MiSuscripcion;
